// src/pages/VulnerabilityScans.jsx - Main Vulnerability Scans Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { vulnerabilityApi, clientsApi } from '../services/api';
import { toast } from 'react-toastify';

// Format a DATE value from the API without UTC timezone shifting.
// Flask may serialize MySQL DATE columns as 'YYYY-MM-DD' or RFC 'Mon, 31 Aug 2026 00:00:00 GMT'.
// Always use UTC getters so Eastern timezone never shifts the date back one day.
const formatScanDate = (raw) => {
  if (!raw) return '';
  const s = String(raw);
  // Fast path: plain YYYY-MM-DD — parse manually, no Date object needed
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${parseInt(m)}/${parseInt(d)}/${y}`;
  }
  // Any other format (RFC, ISO with time, etc.) — parse then use UTC getters
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s;
  return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}/${dt.getUTCFullYear()}`;
};

const VulnerabilityScans = () => {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [scanName, setScanName] = useState('');
  const [scanDate, setScanDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFiles, setSelectedFiles] = useState([]);   // array of File objects
  const [uploading, setUploading] = useState(false);
  const [filterClient, setFilterClient] = useState('');

  useEffect(() => {
    loadScans();
    loadClients();
  }, [filterClient]);

  const loadScans = async () => {
    try {
      setLoading(true);
      const params = filterClient ? { client_id: filterClient } : {};
      const response = await vulnerabilityApi.getScans(params);
      // Ensure we always have an array
      const scansData = Array.isArray(response.data) ? response.data : [];
      setScans(scansData);
    } catch (error) {
      toast.error('Failed to load scans');
      setScans([]); // Ensure it's always an array on error
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsApi.getAll();
      // Handle both direct array and paginated response
      const clientsData = Array.isArray(response.data) ? response.data : 
                          (response.data.clients || response.data.items || []);
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClients([]); // Ensure it's always an array on error
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    setSelectedClient('');
    setScanName('');
    setScanDate(new Date().toISOString().split('T')[0]);
    setSelectedFiles([]);
  };

  const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xml', '.txt', '.json', '.nessus', '.html', '.htm'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const valid = [];
    const invalid = [];
    files.forEach(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (ACCEPTED_EXTENSIONS.includes(ext)) valid.push(f);
      else invalid.push(f.name);
    });
    if (invalid.length) {
      toast.error(`Skipped unsupported file(s): ${invalid.join(', ')}. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`);
    }
    if (valid.length) setSelectedFiles(prev => [...prev, ...valid]);
    e.target.value = '';   // reset so the same file can be re-added if needed
  };

  const removeFile = (idx) => setSelectedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!selectedClient || !scanName || !scanDate || selectedFiles.length === 0) {
      toast.error('Please fill all fields and select at least one scan file');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      selectedFiles.forEach(f => formData.append('file', f));
      formData.append('client_id', selectedClient);
      formData.append('scan_name', scanName);
      formData.append('scan_date', scanDate);

      const response = await vulnerabilityApi.uploadScan(formData);
      const data = response.data;
      const count = data.scans ? data.scans.length : 1;
      const warnings = data.warnings || [];

      toast.success(`${count} scan${count !== 1 ? 's' : ''} uploaded successfully!`);
      if (warnings.length) {
        warnings.forEach(w => toast.warning(`${w.filename}: ${w.error}`));
      }
      setUploadDialogOpen(false);
      loadScans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload scan');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (scanId, scanName) => {
    if (!window.confirm(`Are you sure you want to delete "${scanName}"?`)) return;

    try {
      await vulnerabilityApi.deleteScan(scanId);
      toast.success('Scan deleted successfully');
      loadScans();
    } catch (error) {
      toast.error('Failed to delete scan');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'info',
      analyzed: 'success',
      report_generated: 'primary',
      error: 'error'
    };
    return colors[status] || 'default';
  };

  const getSeverityColor = (count, type) => {
    if (count === 0) return '#95a5a6';
    const colors = {
      critical: '#e74c3c',
      high: '#e67e22',
      medium: '#f39c12',
      low: '#3498db',
      info: '#95a5a6'
    };
    return colors[type] || '#95a5a6';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Vulnerability Scans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload and analyze vulnerability scan reports
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadScans}
            sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleUploadClick}
            sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
          >
            Upload New Scan
          </Button>
        </Box>
      </Box>

      {/* Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Client</InputLabel>
            <Select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              label="Filter by Client"
            >
              <MenuItem value="">All Clients</MenuItem>
              {Array.isArray(clients) && clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Scans Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : scans.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <UploadIcon sx={{ fontSize: 64, color: '#bdc3c7', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No vulnerability scans yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload your first vulnerability scan to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleUploadClick}
              sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
            >
              Upload New Scan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#ecf0f1' }}>
              <TableRow>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Scan Name</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Risk Score</strong></TableCell>
                <TableCell><strong>Critical</strong></TableCell>
                <TableCell><strong>High</strong></TableCell>
                <TableCell><strong>Medium</strong></TableCell>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(scans) && scans.map((scan) => (
                <TableRow
                  key={scan.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/vulnerability-scans/${scan.id}`)}
                >
                  <TableCell>{scan.client_name}</TableCell>
                  <TableCell>{scan.scan_name}</TableCell>
                  <TableCell>{formatScanDate(scan.scan_date)}</TableCell>
                  <TableCell>
                    <Chip
                      label={scan.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(scan.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${scan.overall_score}/100`}
                      sx={{
                        bgcolor: scan.overall_score >= 70 ? '#e74c3c' :
                                 scan.overall_score >= 40 ? '#f39c12' : '#27ae60',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={scan.critical_count}
                      sx={{ bgcolor: getSeverityColor(scan.critical_count, 'critical'), color: 'white' }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={scan.high_count}
                      sx={{ bgcolor: getSeverityColor(scan.high_count, 'high'), color: 'white' }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={scan.medium_count}
                      sx={{ bgcolor: getSeverityColor(scan.medium_count, 'medium'), color: 'white' }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{scan.total_vulnerabilities}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/vulnerability-scans/${scan.id}`)}
                      sx={{ color: '#6B3FA0' }}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(scan.id, scan.scan_name)}
                      sx={{ color: '#e74c3c' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#6B3FA0', color: 'white' }}>
          Upload Vulnerability Scan
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Client *</InputLabel>
            <Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              label="Client *"
            >
              {Array.isArray(clients) && clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Scan Name *"
            value={scanName}
            onChange={(e) => setScanName(e.target.value)}
            placeholder="e.g., January 2026 Quarterly Scan"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="date"
            label="Scan Date *"
            value={scanDate}
            onChange={(e) => setScanDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{ mb: 1, borderColor: '#6B3FA0', color: '#6B3FA0', height: '56px' }}
          >
            {selectedFiles.length > 0
              ? `Add more files (${selectedFiles.length} selected)`
              : 'Select Scan File(s) *'}
            <input
              type="file"
              hidden
              multiple
              accept=".csv,.xlsx,.xml,.txt,.json,.nessus,.html,.htm"
              onChange={handleFileChange}
            />
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Accepted: .xlsx, .csv, .xml, .nessus, .json, .txt, .html — multiple files OK
          </Typography>

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {selectedFiles.map((f, idx) => (
                <Alert
                  key={idx}
                  severity="success"
                  sx={{ mb: 0.5, py: 0 }}
                  onClose={() => removeFile(idx)}
                >
                  {f.name} ({(f.size / 1024).toFixed(0)} KB)
                </Alert>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
            sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VulnerabilityScans;
